import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { GameStatus, GameType, IGame } from '@courses/interfaces';
import { Game } from '@courses/entities';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  gamesSubject: BehaviorSubject<{ [courseId: number]: { [id: number]: Game } }>;

  constructor(private http: HttpClient) {
    this.gamesSubject = new BehaviorSubject<{ [courseId: number]: { [id: number]: Game } }>(null);
  }

  getTypes() {
    return of([
      { id: GameType.AUTO, name: 'Autosim Game' },
      { id: GameType.COMP, name: 'Competitive Game' },
      { id: GameType.QUIZ, name: 'Quiz' },
      { id: GameType.TEMP, name: 'Temporary' },
    ]);
  }

  getStatuses() {
    return of([
      { id: GameStatus.PENDING, name: 'Pending' },
      { id: GameStatus.PUBLISHED, name: 'Published' },
      { id: GameStatus.UNPUBLISHED, name: 'Unpublished' },
      { id: GameStatus.WARMEDUP, name: 'Warmedup' },
    ]);
  }

  updateGamesSubject(courseId: number, game: IGame) {
    this.gamesSubject.next({
      ...(this.gamesSubject.value ? this.gamesSubject.value : {}),
      [courseId]: {
        ...(this.gamesSubject.value && courseId in this.gamesSubject.value
          ? this.gamesSubject.value[courseId]
          : {}),
        [game.id]: new Game(game),
      },
    });
  }

  getGame(courseId: number, id: number): Observable<Game> {
    if (
      this.gamesSubject.value &&
      courseId in this.gamesSubject.value &&
      id in this.gamesSubject.value[courseId]
    ) {
      return this.gamesSubject.pipe(
        map(games => games[courseId][id]),
        first()
      );
    }

    return this.http.get<any>(`${environment.apiUrl}courses/${courseId}/games/${id}/`).pipe(
      tap((game: IGame) => this.updateGamesSubject(courseId, game)),
      map((game: IGame) => new Game(game))
    );
  }

  fetchGame(courseId: number, id: number): Observable<Game> {
    return this.http.get<any>(`${environment.apiUrl}courses/${courseId}/games/${id}/`).pipe(
      tap((game: IGame) => this.updateGamesSubject(courseId, game)),
      map((game: IGame) => new Game(game))
    );
  }

  createGame(courseId: number, game: IGame): Observable<Game> {
    return this.http
      .post<any>(`${environment.apiUrl}courses/${courseId}/games/`, game)
      .pipe(tap(game => this.updateGamesSubject(courseId, game)));
  }

  updateGame(courseId: number, id: number, gameConfig: any) {
    return this.http
      .patch<any>(`${environment.apiUrl}courses/${courseId}/games/${id}/`, gameConfig)
      .pipe(
        tap(game => this.updateGamesSubject(courseId, game)),
        map((game: IGame) => new Game(game))
      );
  }

  deleteGame(courseId: number, id: number) {
    if (
      this.gamesSubject.value &&
      courseId in this.gamesSubject.value &&
      id in this.gamesSubject.value[courseId]
    ) {
      const games = this.gamesSubject.value;

      delete games[courseId][id];
      setTimeout(() => this.gamesSubject.next(games));
    }

    return this.http.delete<any>(`${environment.apiUrl}courses/${courseId}/games/${id}/`, {
      observe: 'response',
    });
  }

  simulate(courseId: number, id: number): Observable<string> {
    return this.http
      .post<any>(`${environment.apiUrl}courses/${courseId}/games/${id}/simulate/`, { async: true })
      .pipe(
        tap(_ => {
          if (
            this.gamesSubject.value &&
            courseId in this.gamesSubject.value &&
            id in this.gamesSubject.value[courseId]
          ) {
            this.gamesSubject.value[courseId][id].isSimulating = true;
          }
        })
      );
  }

  rollback(courseId: number, id: number, targetPeriod: number, erase: boolean): Observable<Game> {
    return this.http
      .post<any>(`${environment.apiUrl}courses/${courseId}/games/${id}/rollback/`, {
        targetPeriod,
        erase,
      })
      .pipe(
        tap(game => this.updateGamesSubject(courseId, game)),
        map((game: IGame) => new Game(game))
      );
  }

  getMailTo(courseId: number, id: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}courses/${courseId}/games/${id}/contact/`);
  }

  getTask(courseId: number, id: string): Observable<any> {
    return this.http
      .get<any>(`${environment.apiUrl}task_result/${id}/`, {
        headers: { 'silent-loader': 'true' },
      })
      .pipe(
        tap(res => {
          if (res.status === 'SUCCESS') {
            this.updateGamesSubject(courseId, res.data);
          }
        })
      );
  }
}
